import { useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle, Phone, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "./shared";
import { api, WHATSAPP_URL, formatApiError } from "@/lib/api";

const SERVICES = [
  "Vazamento de gás",
  "Instalação de aquecedor",
  "Conversão de fogão",
  "Tubulação de gás",
  "Teste de estanqueidade",
  "Laudo técnico",
  "Manutenção preventiva",
  "Outro",
];

export const Contact = () => {
  const [form, setForm] = useState({ name: "", phone: "", service: SERVICES[0], message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/leads", form);
      toast.success("Pedido recebido! Nossa equipe entrará em contato em instantes.");
      setForm({ name: "", phone: "", service: SERVICES[0], message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-white/10 bg-[var(--dark-bg)] px-4 py-3.5 text-sm text-white placeholder-zinc-500 outline-none transition-colors focus:border-[var(--primary)]";

  return (
    <section id="contato" className="hero-grid hero-fire bg-[var(--dark-bg)] py-24 sm:py-32" data-testid="contact-section">
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 lg:grid-cols-2 lg:px-8">
        <div>
          <Reveal>
            <span className="font-display text-sm font-bold uppercase tracking-[0.35em] text-[var(--primary)]">
              10 — Emergência?
            </span>
            <h2 className="mt-3 font-display text-4xl font-bold uppercase leading-[0.95] text-white sm:text-6xl">
              Está sentindo <span className="italic text-[var(--primary)]">cheiro de gás?</span>
            </h2>
            <p className="mt-3 font-display text-2xl font-bold uppercase italic text-white">
              Não coloque sua família em risco.
            </p>
            <p className="mt-4 max-w-md text-base text-zinc-400">
              Nossa equipe está pronta para te atender agora. Chame no WhatsApp ou deixe seus dados
              que retornamos em minutos.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="mt-8 flex flex-wrap items-center gap-4">
            <motion.a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-whatsapp-button"
              animate={{ scale: [1, 1.04, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="flex items-center gap-2.5 rounded-full bg-[var(--whatsapp)] px-7 py-4 font-display text-base font-bold uppercase tracking-wider text-black shadow-[0_8px_40px_rgba(37,211,102,0.4)]"
            >
              <MessageCircle size={19} strokeWidth={2.5} />
              Chamar agora no WhatsApp
            </motion.a>
          </Reveal>
          <Reveal delay={0.25} className="mt-8 space-y-3">
            <a href="tel:+5511988891096" data-testid="contact-phone-link" className="flex items-center gap-3 text-zinc-300 transition-colors hover:text-[var(--primary)]">
              <Phone size={18} className="text-[var(--primary)]" /> (11) 98889-1096
            </a>
            <a href="mailto:contato@Abrinstalmanutentudo.com.br" data-testid="contact-email-link" className="flex items-center gap-3 text-zinc-300 transition-colors hover:text-[var(--primary)]">
              <Mail size={18} className="text-[var(--primary)]" /> contato@Abrinstalmanutentudo.com.br
            </a>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="rounded-3xl border border-white/10 bg-[#12141A] p-8 shadow-2xl sm:p-10"
          >
            <h3 className="font-display text-2xl font-bold uppercase text-white">
              Solicitar <span className="italic text-[var(--primary)]">visita técnica</span>
            </h3>
            <p className="mt-1 text-sm text-zinc-500">Preencha e retornamos em instantes.</p>
            <div className="mt-7 space-y-4">
              <input
                required
                data-testid="contact-name-input"
                placeholder="Seu nome *"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={inputCls}
              />
              <input
                required
                data-testid="contact-phone-input"
                placeholder="Telefone / WhatsApp *"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className={inputCls}
              />
              <select
                data-testid="contact-service-select"
                value={form.service}
                onChange={(e) => setForm({ ...form, service: e.target.value })}
                className={inputCls}
              >
                {SERVICES.map((s) => (
                  <option key={s} value={s} className="bg-[var(--dark-bg)]">
                    {s}
                  </option>
                ))}
              </select>
              <textarea
                data-testid="contact-message-input"
                placeholder="Descreva o que está acontecendo (opcional)"
                rows={3}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className={`${inputCls} resize-none`}
              />
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-button"
                className="flex w-full items-center justify-center gap-2 rounded-full bg-[var(--primary)] py-4 font-display text-base font-bold uppercase tracking-wider text-black transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60"
              >
                {sending && <Loader2 size={18} className="animate-spin" />}
                {sending ? "Enviando..." : "Solicitar Visita Técnica"}
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
